import React from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import { TreeViewBox } from '~/components/elements/TreeViewBox'
import {
  CustomerAttributeTreeView,
  type CustomerAttributeTreeViewProps,
} from '~/components/features/CustomerAttributeTreeView'
import {
  type CustomerAttribute as CustomerAttributeBase,
  type CustomerAttributeGroup as CustomerAttributeGroupBase,
} from '~/gql/graphql'

type CustomerAttribute = App.PickGraphQL<CustomerAttributeBase, 'id' | 'name'>
type CustomerAttributeGroup<TCustomerAttribute extends CustomerAttribute> =
  App.PickGraphQL<CustomerAttributeGroupBase, 'id' | 'name'> & {
    customerAttributes: TCustomerAttribute[]
  }

export type CustomerAttributeTreeViewBoxInnerProps<
  TCustomerAttributeGroup extends CustomerAttributeGroup<TCustomerAttribute>,
  TCustomerAttribute extends CustomerAttribute,
> = {
  customerAttributeGroups: TCustomerAttributeGroup[]
  'data-testid'?: string
  onSelectedCustomerAttributeIdsChange?: (
    customerAttributeIds: TCustomerAttributeGroup['id'][],
  ) => void
  selectedCustomerAttributeIds: TCustomerAttributeGroup['id'][]
}

export const CustomerAttributeTreeViewBoxInner = <
  TCustomerAttributeGroup extends CustomerAttributeGroup<TCustomerAttribute>,
  TCustomerAttribute extends CustomerAttribute,
>({
  customerAttributeGroups,
  'data-testid': dataTestId,
  onSelectedCustomerAttributeIdsChange,
  selectedCustomerAttributeIds,
}: CustomerAttributeTreeViewBoxInnerProps<
  TCustomerAttributeGroup,
  TCustomerAttribute
>) => {
  const { t } = useTranslation()
  const unselectedCustomerAttributeGroups = React.useMemo(
    () =>
      customerAttributeGroups
        .map((customerAttributeGroup) => ({
          ...customerAttributeGroup,
          customerAttributes: customerAttributeGroup.customerAttributes.filter(
            (customerAttribute) =>
              !selectedCustomerAttributeIds.includes(customerAttribute.id),
          ),
        }))
        .filter(
          (customerAttributeGroup) =>
            0 < customerAttributeGroup.customerAttributes.length,
        ),
    [customerAttributeGroups, selectedCustomerAttributeIds],
  )

  const selectedCustomerAttributeGroups = React.useMemo(
    () =>
      customerAttributeGroups
        .map((customerAttributeGroup) => ({
          ...customerAttributeGroup,
          customerAttributes: customerAttributeGroup.customerAttributes.filter(
            (customerAttribute) =>
              selectedCustomerAttributeIds.includes(customerAttribute.id),
          ),
        }))
        .filter(
          (customerAttributeGroup) =>
            0 < customerAttributeGroup.customerAttributes.length,
        ),
    [customerAttributeGroups, selectedCustomerAttributeIds],
  )

  const handleUnselectedTreeViewItemClick = React.useCallback<
    EH<
      CustomerAttributeTreeViewProps<
        TCustomerAttributeGroup,
        TCustomerAttribute
      >,
      'onItemClick'
    >
  >(
    (customerAttributeIds) => {
      const newCustomerAttributeIds = customerAttributeGroups
        .flatMap(R.prop('customerAttributes'))
        .filter(({ id }) =>
          [...selectedCustomerAttributeIds, ...customerAttributeIds].includes(
            id,
          ),
        )
        .map(R.prop('id'))

      R.pipe(
        customerAttributeGroups,
        R.flatMap(R.prop('customerAttributes')),
        R.filter(({ id }) =>
          [...selectedCustomerAttributeIds, ...customerAttributeIds].includes(
            id,
          ),
        ),
        R.map(R.prop('id')),
      )
      onSelectedCustomerAttributeIdsChange?.(newCustomerAttributeIds)
    },
    [
      customerAttributeGroups,
      onSelectedCustomerAttributeIdsChange,
      selectedCustomerAttributeIds,
    ],
  )

  const handleSelectedTreeViewItemClick = React.useCallback<
    EH<
      CustomerAttributeTreeViewProps<
        TCustomerAttributeGroup,
        TCustomerAttribute
      >,
      'onItemClick'
    >
  >(
    (customerAttributeIds) => {
      const newCustomerAttributeIds = R.difference(
        selectedCustomerAttributeIds,
        customerAttributeIds,
      )
      onSelectedCustomerAttributeIdsChange?.(newCustomerAttributeIds)
    },
    [onSelectedCustomerAttributeIdsChange, selectedCustomerAttributeIds],
  )

  return (
    <TreeViewBox
      data-testid={dataTestId}
      selectedTitle={t('selected_customized_segments')}
      selectedTreeView={
        <CustomerAttributeTreeView
          customerAttributeGroups={selectedCustomerAttributeGroups}
          onItemClick={handleSelectedTreeViewItemClick}
        />
      }
      unselectedTitle={t('unselected_customized_segments')}
      unselectedTreeView={
        <CustomerAttributeTreeView
          customerAttributeGroups={unselectedCustomerAttributeGroups}
          onItemClick={handleUnselectedTreeViewItemClick}
        />
      }
    />
  )
}
